import type { SelectProps, CheckboxProps } from 'antd'
import { getMeetingList } from 'api'
import { MeetingProgress, type MeetingModel } from 'api/meeting-model'
import { DefaultOptionType } from 'antd/es/select'
import useUserStore from 'store/userStore'
import { ymdhms } from 'utils/date-format'
import { useIntl } from "react-intl"

interface MeetingSelectHandler {
  getCurrentData: (value?: string) => MeetingModel | undefined
}

interface MeetOption extends DefaultOptionType {
  meeting: MeetingModel
}

interface MeetingSelectProps extends SelectProps {
  autoSelect?: boolean
  onLoadFinish?: () => Promise<any>
  onMeetChange?: (value: string, option?: MeetOption) => void
  onlyUnStarted?: boolean
  defaultValue?:number|undefined,
  notEnded?:boolean
}

function MeetingSelect(
  {
    value,
    onChange,
    autoSelect,
    onLoadFinish,
    onMeetChange,
    onlyUnStarted = false,
    defaultValue = undefined,
    notEnded = false,
    ...rest
  }: MeetingSelectProps,
  ref: Ref<MeetingSelectHandler>
) {
  const intl = useIntl()

  const { loginInfo } = useUserStore()
  const [options, setOptions] = useState<MeetOption[]>([])
  const [list, setList] = useState<MeetingModel[]>([])
  const [currentValue, setCurrentValue] = useState<string>(value)
  const [unstartedFlag, setUnstartedFlag] = useState(onlyUnStarted)
  const [showUnstartedMeeting, setShowUnstartedMeeting] = useState(defaultValue)
  const styles: SelectProps['styles'] = {
    popup: {
      root: {
        minWidth: '1050px'
      }
    }
  }
  const { loading } = useWatcher(
    getMeetingList({
      pageNum: 0,
      pageSize: 10000,
      meeting_progress: showUnstartedMeeting,
      user_id: loginInfo?.loginUser.id
    }),
    [unstartedFlag],
    {
      immediate: true
    }
  ).onSuccess(({ data: { data: { rows } } }) => {

    let optionList=[];

    for (let index = 0; index < rows.length; index++) {
      const element = rows[index];

      // console.log(notEnded,element.meeting_progress,MeetingProgress.END,1232);

      if (notEnded == true) {
        if (element.meeting_progress! < MeetingProgress.END) {
          optionList.push({
            label: `${element.meeting_name_zh}/${element.meeting_name_en}`,
            value: element.meeting_id,
            meeting: element
          });
        }
      }
      else {
        optionList.push({
          label: `${element.meeting_name_zh}/${element.meeting_name_en}`,
          value: element.meeting_id,
          meeting: element
        });
      }
    }

    setOptions(optionList)
    setList(rows)
    if (autoSelect) {
      const selectOption = optionList[0]
      changeHandler(selectOption?.value ?? '', selectOption)
    }
    setTimeout(async () => await onLoadFinish?.(), 0)
  })

  const changeHandler = (value: string, option?: any) => {
    // console.log(value,option)
    setCurrentValue(value)
    onChange?.(value, option)
    onMeetChange?.(value, option)
  }

  useImperativeHandle(
    ref,
    () => ({
      getCurrentData(value: string = currentValue) {
        return list.find(({ meeting_id }) => meeting_id === value)
      }
    }),
    [list, currentValue]
  )

  const unstartedFlagChangeHandler: CheckboxProps['onChange'] = (e) => {
    if(e.target.checked)
    {
      setShowUnstartedMeeting(0);
    }
    else
    {
      setShowUnstartedMeeting(undefined);
    }

    setUnstartedFlag(e.target.checked)
  }

  return (
    <Select<string, MeetOption>
      allowClear
      showSearch
      optionFilterProp="label"
      {...rest}
      styles={styles}
      options={options}
      loading={loading}
      value={value}
      onChange={changeHandler}
      popupRender={
        onlyUnStarted
          ? undefined
          : (menu) => (
              <>
                {menu}
                <Divider style={{ margin: '8px 0' }} />
                <div className="tw-p-2">
                  <Checkbox
                    checked={unstartedFlag}
                    onChange={unstartedFlagChangeHandler}
                  >
                    {intl.formatMessage({ id: "table.meeting.only.not.started" })}
                  </Checkbox>
                </div>
              </>
            )
      }
      optionRender={({ label, value, data }) => (
        <>
          <Descriptions
            classNames={{
              content:
                'tw-text-ellipsis tw-whitespace-nowrap tw-overflow-hidden'
            }}
            column={8}
          >
            <Descriptions.Item span={4} label={intl.formatMessage({ id: "common.title" })}>
              {label}
            </Descriptions.Item>
            <Descriptions.Item span={3} label={intl.formatMessage({ id: "common.time" })}>
              {dayjs(data.meeting.start_time).format(ymdhms)}-
              {dayjs(data.meeting.end_time).format(ymdhms)}
            </Descriptions.Item>
            <Descriptions.Item span={1} label="code">
              {value}
            </Descriptions.Item>
          </Descriptions>
        </>
      )}
    />
  )
}

export default forwardRef(MeetingSelect)

export type { MeetingSelectHandler, MeetOption, MeetingSelectProps }
